import { formatAmount, formatPercent } from "../../helpers/quickbooks";
import { useIsMobile } from "../../helpers/useIsMobile";

const formatValue = (row, value) => {
    if (row.kind === "section") return "";
    if (value === null || value === undefined) return "-";
    if (row.format === "percent") {
        return value === 0 ? "0.00%" : formatPercent(Number(value) * 100);
    }
    if (row.format === "ratio") return Number(value).toFixed(2);
    if (Number(value) === 0) return "$0.00";
    return formatAmount(value);
};

const rowBackgroundClass = (row) => (
    row.kind === "section" || row.kind === "ratio" ? "bg-gray-50" : "bg-white"
);

export const ProfitAndLossStatementTable = ({ payload }) => {
    const isMobile = useIsMobile();
    const months = payload?.meta?.months || [];
    const rows = payload?.rows || [];
    const totalLabel = payload?.meta?.totalLabel || "Total";
    const fallbackMonths = months.filter((month) => month.usedFullMonthFallback);

    return (
        <>
            <div className="min-w-0 max-w-full w-full overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className={`${isMobile ? "" : "sticky left-0 z-20"} bg-gray-50 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500`}>
                                Account
                            </th>
                            {months.map((month) => (
                                <th
                                    key={month.key}
                                    className="whitespace-nowrap bg-gray-50 px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-zinc-500"
                                >
                                    {month.label}
                                </th>
                            ))}
                            <th className={`${isMobile ? "" : "sticky right-0 z-20"} whitespace-nowrap bg-gray-50 px-4 py-2 text-right text-xs font-medium uppercase tracking-wider text-zinc-500 ${isMobile ? "" : "shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.25)]"}`}>
                                {totalLabel}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {rows.map((row) => {
                            const background = rowBackgroundClass(row);
                            const labelClass = row.kind === "section"
                                ? "text-xs font-bold uppercase tracking-wider text-stone-900"
                                : row.kind === "subtotal"
                                    ? "font-bold text-stone-900"
                                    : row.kind === "ratio"
                                        ? "text-blue-600"
                                        : "text-stone-900";
                            const valueClass = (value) => row.kind === "ratio"
                                ? "text-blue-600"
                                : Number(value) < 0
                                    ? "text-red-500"
                                    : "text-stone-900";
                            return (
                                <tr key={row.key} className={`hover:bg-gray-50 ${background}`}>
                                    <td
                                        className={`${isMobile ? "" : "sticky left-0 z-10"} whitespace-nowrap px-4 py-2 text-left ${background} ${labelClass}`}
                                        style={{ paddingLeft: `${24 + (row.level || 0) * 24}px` }}
                                    >
                                        {row.label}
                                    </td>
                                    {months.map((month) => (
                                        <td
                                            key={`${row.key}-${month.key}`}
                                            className={`whitespace-nowrap px-4 py-2 text-right tabular-nums ${background} ${valueClass(row.months?.[month.key])} ${row.kind === "subtotal" ? "font-bold" : ""}`}
                                        >
                                            {formatValue(row, row.months?.[month.key])}
                                        </td>
                                    ))}
                                    <td
                                        className={`${isMobile ? "" : "sticky right-0 z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.25)]"} whitespace-nowrap px-4 py-2 text-right tabular-nums ${background} ${valueClass(row.total)} ${row.kind === "subtotal" ? "font-bold" : ""}`}
                                    >
                                        {formatValue(row, row.total)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {fallbackMonths.length > 0 && (
                <p className="mt-2 text-xs text-zinc-500">
                    {fallbackMonths.map((month) => month.label).join(", ")} reflects the full month because day-level detail was unavailable.
                </p>
            )}
        </>
    );
};

export default ProfitAndLossStatementTable;
